import { Component, inject, OnInit, signal, ChangeDetectionStrategy } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogModule } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatTooltipModule } from '@angular/material/tooltip';
import { Router } from '@angular/router';
import {
  KnowledgebaseService,
  KnowledgebaseCategory,
} from '@awo-care/shared-core';
import { firstValueFrom } from 'rxjs';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { renderKbArticleHtml } from './knowledgebase-dialog.utils';

export interface KnowledgebaseDialogData {
  docId: string;
  highlightText?: string;
}

@Component({
  selector: 'awo-care-knowledgebase-dialog',
  standalone: true,
  imports: [
    MatDialogModule,
    MatIconModule,
    MatButtonModule,
    MatProgressSpinnerModule,
    MatTooltipModule,
  ],
  template: `
    <h2 mat-dialog-title>
      @if (title()) {
        {{ title() }}
      } @else {
        Artikel laden...
      }
    </h2>
    <mat-dialog-content>
      @if (loading()) {
        <div class="loading-container">
          <mat-spinner diameter="40" />
        </div>
      } @else if (error()) {
        <p class="error-text">{{ error() }}</p>
      } @else {
        <div class="article-meta">
          <span class="category-badge" [class]="category()">
            {{ category() === 'customer' ? 'Eltern-Doku' : 'Mitarbeiter-Doku' }}
          </span>
        </div>
        <div class="article-content" [innerHTML]="renderedContent()"></div>
      }
    </mat-dialog-content>
    <mat-dialog-actions align="end">
      <button mat-button mat-dialog-close>Schließen</button>
      @if (!loading() && !error()) {
        <button mat-flat-button color="primary" (click)="editArticle()" matTooltip="Artikel bearbeiten">
          <mat-icon>edit</mat-icon>
          Bearbeiten
        </button>
      }
    </mat-dialog-actions>
  `,
  changeDetection: ChangeDetectionStrategy.Eager,
  styles: [
    `
      .loading-container {
        display: flex;
        justify-content: center;
        padding: 2rem;
      }

      .error-text {
        color: var(--mat-sys-error, #b00020);
        text-align: center;
        padding: 1rem;
      }

      .article-meta {
        margin-bottom: 1rem;
      }

      .category-badge {
        display: inline-block;
        padding: 0.125rem 0.5rem;
        border-radius: 0.25rem;
        font-size: 0.75rem;
        font-weight: 600;
      }

      .category-badge.customer {
        background: rgba(33, 150, 243, 0.15);
        color: #1976d2;
      }

      .category-badge.employee {
        background: rgba(76, 175, 80, 0.15);
        color: #388e3c;
      }

      .article-content {
        line-height: 1.6;
        font-size: 0.875rem;
      }

      .article-content :first-child {
        margin-top: 0;
      }

      .article-content ::ng-deep .grounding-highlight {
        background: rgba(255, 152, 0, 0.2);
        border-bottom: 0.125rem solid rgba(255, 152, 0, 0.6);
        padding: 0.0625rem 0;
      }

      mat-dialog-content {
        max-height: 70vh;
        min-width: 300px;
      }

      @media (min-width: 48rem) {
        mat-dialog-content {
          min-width: 500px;
        }
      }
    `,
  ],
})
export class KnowledgebaseDialogComponent implements OnInit {
  private readonly data = inject<KnowledgebaseDialogData>(MAT_DIALOG_DATA);
  private readonly router = inject(Router);
  private readonly kbService = inject(KnowledgebaseService);
  private readonly sanitizer = inject(DomSanitizer);

  readonly loading = signal(true);
  readonly error = signal<string | null>(null);
  readonly title = signal('');
  readonly category = signal<KnowledgebaseCategory>('customer');
  readonly renderedContent = signal<SafeHtml>('');

  async ngOnInit(): Promise<void> {
    try {
      const article = await firstValueFrom(
        this.kbService.getArticle(this.data.docId)
      );
      if (!article) {
        this.error.set('Artikel nicht gefunden.');
        return;
      }
      this.title.set(article.title);
      this.category.set(article.category);

      // renderKbArticleHtml filtert per DOMPurify — erst danach ist der Bypass sicher
      this.renderedContent.set(
        this.sanitizer.bypassSecurityTrustHtml(
          renderKbArticleHtml(
            article.content,
            article.category,
            this.data.highlightText
          )
        )
      );
    } catch {
      this.error.set('Fehler beim Laden des Artikels.');
    } finally {
      this.loading.set(false);
    }
  }

  editArticle(): void {
    const url = this.router.serializeUrl(
      this.router.createUrlTree(['/knowledgebase', this.data.docId])
    );
    window.open(url, '_blank');
  }
}
